import React from 'react';
import { Dialog } from 'primereact/dialog';
import { ANDADetailModalProps, HistoryTimelineProps } from './ANDADetailModal.types';
import './ANDADetailModal.styles.scss';
import 'primereact/resources/themes/lara-light-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

/**
 * Format date string for display
 */
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: '2-digit',
    });
  } catch {
    return dateString;
  }
};

/**
 * History Timeline Component
 * Displays ANDA status history as a visual timeline
 */
const HistoryTimeline: React.FC<HistoryTimelineProps> = ({ history }) => {
  if (!history || history.length === 0) {
    return <div className="no-history">No history available</div>;
  }

  return (
    <div className="history-timeline">
      {history.map((entry, index) => {
        const statusClass = entry.status === 'Active' ? 'active' : 'dormant';
        return (
          <div key={index} className="timeline-item">
            <div className={`timeline-node ${statusClass}`}>
              <i className={`pi ${entry.status === 'Active' ? 'pi-check' : 'pi-times'}`}></i>
            </div>
            <div className="timeline-content">
              <span className={`timeline-status ${statusClass}`}>{entry.status}</span>
              <span className="timeline-date">{formatDate(entry.timestamp)}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * ANDA Detail Modal Component
 * Reusable modal for displaying ANDA information with history timeline.
 * Can be used across Landing Page, Molecule Details, and Competitors Tab.
 */
const ANDADetailModal: React.FC<ANDADetailModalProps> = ({ visible, onHide, data }) => {
  // Render description with line breaks
  const renderDescription = () => {
    if (!data?.description) return null;
    const lines = data.description.split('\n');
    return lines.map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < lines.length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Handle external link click
  const handleLinkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data?.link) {
      window.open(data.link, '_blank', 'noopener,noreferrer');
    }
  };

  // Extract ANDA number from title for colored display
  const getAndaNumberFromTitle = (title: string): { andaNumber: string; rest: string } => {
    const parts = title.split(' - ');
    if (parts.length > 1) {
      return {
        andaNumber: parts[0],
        rest: parts.slice(1).join(' - '),
      };
    }
    return { andaNumber: title, rest: '' };
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={null}
      className="anda-detail-modal"
      modal
      dismissableMask
      draggable={false}
      resizable={false}
      style={{ width: '90vw', maxWidth: '900px' }}
      baseZIndex={10000}
    >
      {data ? (
        <div className="anda-detail-modal-content">
          {/* Header with Title and Expiration Date */}
          <div className="anda-detail-modal-header">
            <div className="anda-title-with-link">
              <h2 className="anda-detail-modal-title">
                <span className="anda-number" style={{ color: data.color || '#374151' }}>
                  {getAndaNumberFromTitle(data.title).andaNumber}
                </span>
                {getAndaNumberFromTitle(data.title).rest && (
                  <span> - {getAndaNumberFromTitle(data.title).rest}</span>
                )}
              </h2>
            </div>
            <div className="anda-detail-modal-date">
              <span className="date-label">Approval Date</span>
              {data.date}
            </div>
          </div>

          {/* Description - Concept Name - Strength + Additional Info */}
          <div className="anda-detail-modal-description">
            <div className="description-text">{renderDescription()}</div>
            <div className="description-info-grid">
              <div className="info-item">
                <span className="info-label">Marketing Status:</span>
                <span className="info-value">{data.marketing_status || '-'}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Submission Date:</span>
                <span className="info-value">
                  {data.last_submission_date ? formatDate(data.last_submission_date) : '-'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Last Submission Type:</span>
                <span className="info-value">{data.last_submission_type || '-'}</span>
              </div>
            </div>
          </div>

          {/* History Timeline Section */}
          <div className="anda-history-section">
            <HistoryTimeline history={data.history} />
          </div>

          {/* Footer with Tags and Link */}
          <div className="anda-detail-modal-footer">
            <div className="anda-detail-modal-tags">
              {/* ANDA Status Tag */}
              {data.status && <span className="anda-detail-modal-tag normal">{data.status}</span>}
              {/* ANDA Applicant Tag */}
              {data.applicant && (
                <span className="anda-detail-modal-tag normal">{data.applicant}</span>
              )}
              {/* TE Code Tag */}
              {data.te_code && (
                <span className="anda-detail-modal-tag normal">TE: {data.te_code}</span>
              )}
            </div>

            {/* ANDA Link Icon */}
            {data.link && (
              <button
                className="anda-link-icon"
                onClick={handleLinkClick}
                aria-label="Open link in new tab"
                title="Open link in new tab"
              >
                <i className="pi pi-external-link"></i>
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="anda-detail-modal-content">
          <div>No data available</div>
        </div>
      )}
    </Dialog>
  );
};

export default ANDADetailModal;
